import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch } from '../../../lib/api';
import { showToast } from '../../../components/Toast';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { API_DOCS } from './ConfigurazioneDocumentiPage.api';
import './ConfigurazioneDocumentiPage.css';

const MODULO = 'commerciale.configurazione_documenti';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type Famiglia = 'DDT_VENDITA' | 'DDT_CANTIERE' | 'SAL' | 'FATTURA';

const FAMIGLIE: Famiglia[] = ['DDT_VENDITA', 'DDT_CANTIERE', 'SAL', 'FATTURA'];

const FAMIGLIA_LABEL: Record<Famiglia, string> = {
  DDT_VENDITA: 'DDT di vendita',
  DDT_CANTIERE: 'DDT su cantiere',
  SAL: 'SAL',
  FATTURA: 'Fatture (predisposto)',
};

const CAUSALE_TIPO_LABEL: Record<string, string> = {
  SCARICO: 'Scarico',
  TRASFERIMENTO: 'Trasferimento',
};

const CAUSALE_TIPO_BADGE: Record<string, string> = {
  SCARICO: 'bop-badge-warning',
  TRASFERIMENTO: 'bop-badge-info',
};

function isFamigliaDdt(f: Famiglia): boolean {
  return f === 'DDT_VENDITA' || f === 'DDT_CANTIERE';
}

interface CausaleRef {
  id: number;
  codice: string;
  descrizione: string;
  tipo: string;
}

interface TipoDocumento {
  id: number;
  societaId: number;
  codice: string;
  nome: string;
  famiglia: Famiglia;
  causaleMovimentoId: number | null;
  isDefault: boolean;
  attivo: boolean;
  note: string | null;
  causaleMovimento: CausaleRef | null;
  _count?: { ddt: number; sal: number };
}

interface CausaleMagazzino {
  id: number;
  societaId: number;
  codice: string;
  descrizione: string;
  tipo: string;
  attivo: boolean;
}

interface FormState {
  id: number | null;
  codiceOriginale: string;
  codice: string;
  nome: string;
  famiglia: Famiglia;
  famigliaOriginale: Famiglia | null;
  causaleMovimentoId: string;
  isDefault: boolean;
  note: string;
  documentiCollegati: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function docCount(t: TipoDocumento): number {
  return (t._count?.ddt ?? 0) + (t._count?.sal ?? 0);
}

// Icona "riattiva" (check in cerchio) — non presente in RowIcon
function IconRiattiva() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="8 12 11 15 16 9" />
    </svg>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function ConfigurazioneDocumentiPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');

  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [items, setItems] = useState<TipoDocumento[]>([]);
  const [causali, setCausali] = useState<CausaleRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [azioneInCorso, setAzioneInCorso] = useState(false);

  const [form, setForm] = useState<FormState | null>(null);
  const [formSnapshot, setFormSnapshot] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const isFormDirty = form !== null && formSnapshot !== '' && JSON.stringify(form) !== formSnapshot;
  useDirtyForm(isFormDirty, 'Configurazione tipo documento');

  const load = async () => {
    if (!pronta) return;
    if (!societaId) { setItems([]); setCausali([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const [rTipi, rCausali] = await Promise.all([
        // @api GET /api/tipi-documento?societaId= -> {"success":true,"data":[{"id":"number","societaId":"number","codice":"string","nome":"string","famiglia":"DDT_VENDITA|DDT_CANTIERE|SAL|FATTURA","causaleMovimentoId":"number|null","isDefault":"boolean","attivo":"boolean","note":"string|null","causaleMovimento":{"id":"number","codice":"string","descrizione":"string","tipo":"string"},"_count":{"ddt":"number","sal":"number"}}]}
        apiFetch<{ success: boolean; data: TipoDocumento[] }>(`/api/tipi-documento?societaId=${societaId}`),
        // @api GET /api/causali-movimento?societaId= -> {"success":true,"data":[{"id":"number","societaId":"number","codice":"string","descrizione":"string","tipo":"CARICO|SCARICO|TRASFERIMENTO|RETTIFICA","attivo":"boolean"}]}
        apiFetch<{ success: boolean; data: CausaleMagazzino[] }>(`/api/causali-movimento?societaId=${societaId}`),
      ]);
      setItems(rTipi.data ?? []);
      // Per il select causale servono solo le causali attive di tipo SCARICO o TRASFERIMENTO
      setCausali(
        (rCausali.data ?? [])
          .filter(c => c.attivo && (c.tipo === 'SCARICO' || c.tipo === 'TRASFERIMENTO'))
          .map(c => ({ id: c.id, codice: c.codice, descrizione: c.descrizione, tipo: c.tipo }))
      );
    } catch (err: unknown) {
      setError(estraiErrore(err, 'Errore nel caricamento dei tipi documento'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId, pronta]);

  const perFamiglia = useMemo(() => {
    const map: Record<Famiglia, TipoDocumento[]> = {
      DDT_VENDITA: [], DDT_CANTIERE: [], SAL: [], FATTURA: [],
    };
    for (const t of items) {
      if (map[t.famiglia]) map[t.famiglia].push(t);
    }
    for (const f of FAMIGLIE) {
      map[f].sort((a, b) => a.codice.localeCompare(b.codice));
    }
    return map;
  }, [items]);

  // Causali per il select del form: quelle attive + l'eventuale causale già
  // assegnata al tipo in modifica (anche se nel frattempo disattivata)
  const causaliSelect = useMemo(() => {
    if (!form || !form.causaleMovimentoId) return causali;
    const selId = Number(form.causaleMovimentoId);
    if (causali.some(c => c.id === selId)) return causali;
    const t = items.find(i => i.causaleMovimento?.id === selId);
    return t?.causaleMovimento ? [...causali, t.causaleMovimento] : causali;
  }, [causali, form, items]);

  // ── Form ────────────────────────────────────────────────────────────────────

  const apriNuovo = () => {
    const nuovo: FormState = {
      id: null,
      codiceOriginale: '',
      codice: '',
      nome: '',
      famiglia: 'DDT_VENDITA',
      famigliaOriginale: null,
      causaleMovimentoId: '',
      isDefault: false,
      note: '',
      documentiCollegati: 0,
    };
    setForm(nuovo);
    setFormSnapshot(JSON.stringify(nuovo));
    setFormError('');
  };

  const apriModifica = (t: TipoDocumento) => {
    const f: FormState = {
      id: t.id,
      codiceOriginale: t.codice,
      codice: t.codice,
      nome: t.nome,
      famiglia: t.famiglia,
      famigliaOriginale: t.famiglia,
      causaleMovimentoId: t.causaleMovimentoId ? String(t.causaleMovimentoId) : '',
      isDefault: t.isDefault,
      note: t.note ?? '',
      documentiCollegati: docCount(t),
    };
    setForm(f);
    setFormSnapshot(JSON.stringify(f));
    setFormError('');
  };

  const chiudi = () => { setForm(null); setFormSnapshot(''); setFormError(''); };

  const setFamiglia = (fam: Famiglia) => {
    setForm(f => f && {
      ...f,
      famiglia: fam,
      // Le famiglie SAL/FATTURA non accettano causale di magazzino
      causaleMovimentoId: isFamigliaDdt(fam) ? f.causaleMovimentoId : '',
    });
  };

  const salva = async () => {
    if (!form) return;
    const codice = form.codice.trim();
    const nome = form.nome.trim();
    if (!codice) { setFormError('Codice obbligatorio'); return; }
    if (!/^[A-Za-z0-9_-]+$/.test(codice)) { setFormError('Il codice può contenere solo lettere, numeri, "-" e "_"'); return; }
    if (!nome) { setFormError('Nome obbligatorio'); return; }

    const causaleId = isFamigliaDdt(form.famiglia) && form.causaleMovimentoId
      ? Number(form.causaleMovimentoId)
      : null;

    setSaving(true);
    setFormError('');
    try {
      if (form.id === null) {
        // @api POST /api/tipi-documento {"societaId":"number","codice":"string","nome":"string","famiglia":"DDT_VENDITA|DDT_CANTIERE|SAL|FATTURA","causaleMovimentoId":"number?","isDefault":"boolean?","note":"string|null"} -> {"success":true,"data":{"id":"number"}}
        await apiFetch<{ success: boolean; data: TipoDocumento }>('/api/tipi-documento', {
          method: 'POST',
          body: JSON.stringify({
            societaId: Number(societaId),
            codice,
            nome,
            famiglia: form.famiglia,
            ...(causaleId !== null ? { causaleMovimentoId: causaleId } : {}),
            isDefault: form.isDefault,
            note: form.note.trim() || null,
          }),
        });
        showToast(`Tipo documento "${codice}" creato`, 'success');
      } else {
        const body: Record<string, unknown> = {
          codice,
          nome,
          causaleMovimentoId: causaleId,
          isDefault: form.isDefault,
          note: form.note.trim() || null,
        };
        // La famiglia si invia solo se cambiata (modificabile solo senza documenti collegati)
        if (form.famigliaOriginale !== form.famiglia) body.famiglia = form.famiglia;
        // @api PUT /api/tipi-documento/:id {"codice":"string?","nome":"string?","famiglia":"string?","causaleMovimentoId":"number|null","isDefault":"boolean?","attivo":"boolean?","note":"string|null"} -> {"success":true,"data":{"id":"number"}}
        await apiFetch<{ success: boolean; data: TipoDocumento }>(`/api/tipi-documento/${form.id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        showToast(`Tipo documento "${codice}" aggiornato`, 'success');
      }
      chiudi();
      load();
    } catch (err: unknown) {
      setFormError(estraiErrore(err, 'Errore nel salvataggio del tipo documento'));
    } finally {
      setSaving(false);
    }
  };

  // ── Azioni riga ─────────────────────────────────────────────────────────────

  const rendiDefault = async (t: TipoDocumento) => {
    setAzioneInCorso(true);
    try {
      // @api PUT /api/tipi-documento/:id {"isDefault":true} -> {"success":true,"data":{"id":"number"}}
      await apiFetch<{ success: boolean; data: TipoDocumento }>(`/api/tipi-documento/${t.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isDefault: true }),
      });
      showToast(`"${t.nome}" è ora il default per ${FAMIGLIA_LABEL[t.famiglia]}`, 'success');
      load();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'impostazione del default"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const toggleAttivo = async (t: TipoDocumento) => {
    if (t.attivo && !window.confirm(`Disattivare il tipo documento ${t.codice} — ${t.nome}?`)) return;
    setAzioneInCorso(true);
    try {
      // @api PUT /api/tipi-documento/:id {"attivo":"boolean"} -> {"success":true,"data":{"id":"number"}}
      await apiFetch<{ success: boolean; data: TipoDocumento }>(`/api/tipi-documento/${t.id}`, {
        method: 'PUT',
        body: JSON.stringify({ attivo: !t.attivo }),
      });
      showToast(`Tipo documento "${t.codice}" ${t.attivo ? 'disattivato' : 'riattivato'}`, 'success');
      load();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel cambio di stato'), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const elimina = async (t: TipoDocumento) => {
    if (!window.confirm(`Eliminare definitivamente il tipo documento ${t.codice} — ${t.nome}?`)) return;
    setAzioneInCorso(true);
    try {
      // @api DELETE /api/tipi-documento/:id -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/tipi-documento/${t.id}`, { method: 'DELETE' });
      showToast(`Tipo documento "${t.codice}" eliminato`, 'success');
      load();
    } catch (err: unknown) {
      // 409 se il tipo ha documenti collegati
      showToast(estraiErrore(err, "Errore nell'eliminazione del tipo documento"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderCausale = (t: TipoDocumento): React.ReactNode => {
    if (!isFamigliaDdt(t.famiglia) || !t.causaleMovimento) {
      return <span className="bop-text-muted">—</span>;
    }
    const c = t.causaleMovimento;
    return (
      <span className="cfgdoc__causale">
        <span className="cfgdoc__causale-testo">{c.codice} — {c.descrizione}</span>
        <span className={`bop-badge ${CAUSALE_TIPO_BADGE[c.tipo] ?? 'bop-badge-neutral'}`}>
          {CAUSALE_TIPO_LABEL[c.tipo] ?? c.tipo}
        </span>
      </span>
    );
  };

  const renderDefault = (t: TipoDocumento): React.ReactNode => {
    if (t.isDefault) return <span className="bop-badge bop-badge-success">Default</span>;
    if (t.attivo && canEdit) {
      return (
        <button
          className="bop-btn bop-btn-secondary bop-btn-sm"
          onClick={e => { e.stopPropagation(); rendiDefault(t); }}
          disabled={azioneInCorso}
        >
          Rendi default
        </button>
      );
    }
    return <span className="bop-text-muted">—</span>;
  };

  const renderTabellaFamiglia = (famiglia: Famiglia): React.ReactNode => {
    const tipi = perFamiglia[famiglia];
    if (tipi.length === 0) {
      return (
        <div className="bop-empty-state">
          Nessun tipo configurato — i documenti di questa famiglia usano il comportamento di default
        </div>
      );
    }
    return (
      <table className={`bop-table ${canEdit ? 'bop-table--clickable' : ''}`.trim()}>
        <thead>
          <tr>
            <th>Codice</th>
            <th>Nome</th>
            <th>Causale magazzino</th>
            <th>Default</th>
            <th className="bop-text-center">Documenti collegati</th>
            <th>Stato</th>
            <th className="bop-table__th--actions"></th>
          </tr>
        </thead>
        <tbody>
          {tipi.map(t => (
            <tr key={t.id} onClick={canEdit ? () => apriModifica(t) : undefined}>
              <td className="cfgdoc__codice">{t.codice}</td>
              <td>{t.nome}</td>
              <td>{renderCausale(t)}</td>
              <td>{renderDefault(t)}</td>
              <td className="bop-text-center">{docCount(t)}</td>
              <td>
                <span className={`bop-badge ${t.attivo ? 'bop-badge-success' : 'bop-badge-neutral'}`}>
                  {t.attivo ? 'Attivo' : 'Disattivo'}
                </span>
              </td>
              <td className="bop-table__td--actions">
                <BopRowActions actions={[
                  { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => apriModifica(t), hidden: !canEdit },
                  { icon: <RowIcon.Disable />, label: 'Disattiva', onClick: () => toggleAttivo(t), variant: 'danger', hidden: !canEdit || !t.attivo },
                  { icon: <IconRiattiva />, label: 'Riattiva', onClick: () => toggleAttivo(t), variant: 'primary', hidden: !canEdit || t.attivo },
                  { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => elimina(t), variant: 'danger', hidden: !canDelete || docCount(t) > 0 },
                ]} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const noSocieta = !societaId;
  const famigliaBloccata = form !== null && form.id !== null && form.documentiCollegati > 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="cfgdoc">
        <div className="bop-page-header">
          <div className="cfgdoc__header-text">
            <h2 className="bop-page-title">Configurazione documenti</h2>
            <p className="bop-text-muted cfgdoc__subtitle">
              Tipi documento del ciclo attivo: causali di magazzino collegate e default per famiglia
            </p>
          </div>
          {canEdit && (
            <button className="bop-btn-primary" onClick={apriNuovo} disabled={noSocieta}>
              + Nuovo tipo
            </button>
          )}
        </div>

        {!isUnica && (
          <div className="bop-filter-bar">
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
        )}

        {error && <div className="bop-inline-error">{error}</div>}

        {noSocieta ? (
          <div className="bop-card">
            <div className="bop-empty-state">
              Seleziona una società per configurare i suoi tipi documento.
            </div>
          </div>
        ) : loading ? (
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento…</div>
          </div>
        ) : (
          <div className="cfgdoc__cards">
            {FAMIGLIE.map(famiglia => (
              <div className="bop-card" key={famiglia}>
                <div className="bop-card__header">
                  <h2 className="bop-card__title">{FAMIGLIA_LABEL[famiglia]}</h2>
                </div>
                <div className="bop-card__body">
                  {renderTabellaFamiglia(famiglia)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modale crea/modifica */}
        {form && (
          <div className="bop-modal-overlay" onClick={chiudi}>
            <div className="bop-modal" onClick={e => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">
                  {form.id === null ? 'Nuovo tipo documento' : `Modifica ${form.codiceOriginale}`}
                </h2>
                <button type="button" className="bop-modal__close" onClick={chiudi} aria-label="Chiudi">×</button>
              </div>
              <div className="bop-modal__body">
                {formError && <div className="bop-inline-error">{formError}</div>}

                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Codice *</label>
                    <input
                      className="bop-input"
                      value={form.codice}
                      onChange={e => setForm(f => f && { ...f, codice: e.target.value })}
                      placeholder="es. DDT-VEN-STD"
                      autoFocus
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Nome *</label>
                    <input
                      className="bop-input"
                      value={form.nome}
                      onChange={e => setForm(f => f && { ...f, nome: e.target.value })}
                      placeholder="es. DDT di vendita standard"
                    />
                  </div>
                </div>

                <div className="bop-form-group">
                  <label className="bop-label">Famiglia *</label>
                  <select
                    className="bop-select"
                    value={form.famiglia}
                    onChange={e => setFamiglia(e.target.value as Famiglia)}
                    disabled={famigliaBloccata}
                  >
                    {FAMIGLIE.map(f => (
                      <option key={f} value={f}>{FAMIGLIA_LABEL[f]}</option>
                    ))}
                  </select>
                  {famigliaBloccata && (
                    <p className="cfgdoc__hint">
                      Famiglia non modificabile: il tipo ha documenti collegati.
                    </p>
                  )}
                </div>

                {isFamigliaDdt(form.famiglia) && (
                  <div className="bop-form-group">
                    <label className="bop-label">Causale di magazzino</label>
                    <select
                      className="bop-select"
                      value={form.causaleMovimentoId}
                      onChange={e => setForm(f => f && { ...f, causaleMovimentoId: e.target.value })}
                    >
                      <option value="">— nessuna —</option>
                      {causaliSelect.map(c => (
                        <option key={c.id} value={String(c.id)}>
                          {c.codice} — {c.descrizione} ({CAUSALE_TIPO_LABEL[c.tipo] ?? c.tipo})
                        </option>
                      ))}
                    </select>
                    <p className="cfgdoc__hint">
                      SCARICO = esce dal deposito; TRASFERIMENTO = va al deposito della commessa/cantiere
                    </p>
                  </div>
                )}

                <div className="bop-form-group">
                  <label className="bop-flex">
                    <input
                      type="checkbox"
                      checked={form.isDefault}
                      onChange={e => setForm(f => f && { ...f, isDefault: e.target.checked })}
                    />
                    <span>&nbsp;Default per la famiglia</span>
                  </label>
                </div>

                <div className="bop-form-group">
                  <label className="bop-label">Note</label>
                  <textarea
                    className="bop-input cfgdoc__note-textarea"
                    rows={2}
                    value={form.note}
                    onChange={e => setForm(f => f && { ...f, note: e.target.value })}
                  />
                </div>
              </div>
              <div className="bop-modal__footer">
                <button type="button" className="bop-btn bop-btn-secondary" onClick={chiudi} disabled={saving}>
                  Annulla
                </button>
                <button type="button" className="bop-btn bop-btn-primary" onClick={salva} disabled={saving}>
                  {saving ? 'Salvo…' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
